import { ChevronsLeft, ChevronsRight } from 'lucide-react';
import { NavLink } from 'react-router-dom';
import { useAtom } from 'jotai';
import { mobileNavOpenAtom } from '@/store/ui';
import { useSidebar } from '@/hooks/use-sidebar';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useContactMessagesUnreadCount } from '@/pages/contact-messages/queries';
import { NAV_SECTIONS, type NavItem } from './nav';

export function Sidebar() {
  const { collapsed, forcedNarrow, setCollapsed } = useSidebar();
  const [mobileOpen, setMobileOpen] = useAtom(mobileNavOpenAtom);

  return (
    <>
      {/* Desktop */}
      <aside
        data-collapsed={collapsed}
        className={cn(
          'bg-sidebar-chrome text-sidebar-foreground border-sidebar-border sticky top-0 hidden h-screen shrink-0 flex-col border-r transition-[width] duration-200 ease-out md:flex',
          collapsed ? 'w-[64px]' : 'w-[232px]',
        )}
      >
        <SidebarBody collapsed={collapsed} onNavigate={() => {}} />
        <div className="border-sidebar-border flex items-center justify-end border-t p-2">
          <button
            type="button"
            onClick={() => setCollapsed((v) => !v)}
            disabled={forcedNarrow}
            className="hover:bg-sidebar-accent hover:text-sidebar-accent-foreground inline-flex size-8 items-center justify-center rounded-md text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={forcedNarrow ? 'Window is too narrow to expand' : undefined}
          >
            {collapsed ? (
              <ChevronsRight className="size-4" />
            ) : (
              <ChevronsLeft className="size-4" />
            )}
          </button>
        </div>
      </aside>

      {/* Mobile drawer */}
      {mobileOpen ? (
        <div className="md:hidden">
          <div
            aria-hidden
            className="fixed inset-0 z-40 bg-black/50"
            onClick={() => setMobileOpen(false)}
          />
          <aside
            className="bg-sidebar-chrome text-sidebar-foreground border-sidebar-border fixed inset-y-0 left-0 z-50 flex w-[260px] flex-col border-r shadow-xl"
            role="dialog"
            aria-label="Navigation"
          >
            <SidebarBody collapsed={false} onNavigate={() => setMobileOpen(false)} />
          </aside>
        </div>
      ) : null}
    </>
  );
}

function SidebarBody({
  collapsed,
  onNavigate,
}: {
  collapsed: boolean;
  onNavigate: () => void;
}) {
  const { data: unreadContact } = useContactMessagesUnreadCount();
  const badges: Record<string, number> = {
    '/contact-messages': unreadContact ?? 0,
  };

  return (
    <>
      <div className="flex h-14 items-center gap-2 px-4">
        <img
          src="/logotype.png"
          alt="Servo"
          width={28}
          height={28}
          className="size-7 shrink-0"
        />
        {!collapsed && (
          <div className="flex flex-col leading-tight">
            <span className="text-sm font-semibold">Servo</span>
            <span className="text-muted-foreground text-[11px]">Admin</span>
          </div>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto px-2 py-2">
        {NAV_SECTIONS.map((section, idx) => (
          <div key={section.label ?? `section-${idx}`} className={idx > 0 ? 'mt-4' : ''}>
            {section.label && !collapsed ? (
              <div className="text-muted-foreground px-3 pb-1 text-[11px] font-semibold uppercase tracking-wide">
                {section.label}
              </div>
            ) : null}
            {section.label && collapsed && idx > 0 ? (
              <div className="border-sidebar-border my-2 border-t" aria-hidden />
            ) : null}
            <ul className="space-y-1">
              {section.items.map((item) => (
                <SidebarLink
                  key={item.to}
                  item={item}
                  collapsed={collapsed}
                  onNavigate={onNavigate}
                  badgeCount={badges[item.to] ?? 0}
                />
              ))}
            </ul>
          </div>
        ))}
      </nav>
    </>
  );
}

function SidebarLink({
  item,
  collapsed,
  onNavigate,
  badgeCount,
}: {
  item: NavItem;
  collapsed: boolean;
  onNavigate: () => void;
  badgeCount: number;
}) {
  const showBadge = badgeCount > 0;
  const badgeLabel = badgeCount > 99 ? '99+' : String(badgeCount);

  const link = (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'group relative flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
          'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
          isActive && 'bg-sidebar-accent text-sidebar-accent-foreground',
          collapsed && 'justify-center px-2',
        )
      }
    >
      <span className="relative shrink-0">
        <item.icon className="size-4" />
        {collapsed && showBadge && (
          <span
            aria-hidden
            className="bg-destructive absolute -right-1.5 -top-1.5 block size-2 rounded-full ring-2 ring-[var(--sidebar)]"
          />
        )}
      </span>
      {!collapsed && (
        <>
          <span className="truncate">{item.label}</span>
          {showBadge && (
            <span
              className="bg-destructive text-destructive-foreground ml-auto inline-flex min-w-[20px] items-center justify-center rounded-full px-1.5 py-0.5 text-[11px] font-semibold leading-none"
              aria-label={`${badgeCount} unread`}
            >
              {badgeLabel}
            </span>
          )}
        </>
      )}
    </NavLink>
  );

  return (
    <li>
      {collapsed ? (
        <Tooltip>
          <TooltipTrigger asChild>{link}</TooltipTrigger>
          <TooltipContent side="right">
            {item.label}
            {showBadge ? ` · ${badgeLabel} unread` : ''}
          </TooltipContent>
        </Tooltip>
      ) : (
        link
      )}
    </li>
  );
}

